import { supabase } from "@/integrations/supabase/client";
import { checkAndAwardBadges } from "./badgeService";
import { awardCpdPoints, getTotalCpdPoints } from "./cpdService";
import { logSimulationActivity, logGoalReached, logStreakMilestone } from "./activityService";

interface GamificationProcessingOptions {
  cpdEnabled?: boolean;
}

/**
 * Process a completed evaluation to award CPD points, check badges, and log activities
 */
export async function processEvaluationGamification(
  userId: string,
  evaluationId: string,
  agentId: string,
  agentName: string,
  score: number,
  goalScore?: number,
  { cpdEnabled = true }: GamificationProcessingOptions = {},
) {
  try {
    if (cpdEnabled) {
      await awardCpdPoints(
        userId,
        5,
        'simulation_completion',
        evaluationId,
        `Completed ${agentName}`
      );
    }

    // 2. Check if goal was reached
    let goalsReachedCount = 0;
    if (goalScore && score >= goalScore) {
      if (cpdEnabled) {
        await awardCpdPoints(
          userId,
          3,
          'goal_achieved',
          evaluationId,
          `Reached goal score on ${agentName}`
        );
      }
      await logGoalReached(userId, agentName, score);
      
      // Count total goals reached
      const { count } = await supabase
        .from('simulation_evaluations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_submitted', true)
        .gte('overall_score', goalScore || 85);
      
      goalsReachedCount = count || 0;
    }

    // 3. Check for perfect score
    if (score === 100) {
      if (cpdEnabled) {
        await awardCpdPoints(
          userId,
          10,
          'perfect_score',
          evaluationId,
          `Perfect score on ${agentName}`
        );
      }
    }

    // 4. Log simulation activity
    await logSimulationActivity(userId, agentName, score, evaluationId);

    // 5. Get current stats for badge checking
    const { count: totalSimulations } = await supabase
      .from('simulation_evaluations')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_submitted', true)
      .is('deleted_at', null);

    const { data: evaluations } = await supabase
      .from('simulation_evaluations')
      .select('overall_score')
      .eq('user_id', userId)
      .eq('is_submitted', true)
      .is('deleted_at', null)
      .order('overall_score', { ascending: false })
      .limit(1);

    const highestScore = evaluations?.[0]?.overall_score || 0;

    const { data: streakData } = await supabase
      .from('user_streaks')
      .select('current_streak')
      .eq('user_id', userId)
      .maybeSingle();

    const currentStreak = streakData?.current_streak || 0;
    const totalCpdPoints = cpdEnabled ? await getTotalCpdPoints(userId) : 0;

    // 6. Check and award new badges
    const newBadges = await checkAndAwardBadges({
      userId,
      totalSimulations: totalSimulations || 0,
      totalCoachingSessions: 0,
      goalsReached: goalsReachedCount,
      highestScore,
      highestReadinessScore: 0,
      currentStreak,
      totalCpdPoints,
    }, { cpdEnabled });

    // 7. Log streak milestones
    if (currentStreak > 0 && [7, 14, 30, 60, 100].includes(currentStreak)) {
      await logStreakMilestone(userId, currentStreak);
    }

    return { 
      success: true, 
      newBadges, 
      cpdPointsEarned: cpdEnabled
        ? (score === 100 ? 18 : (goalScore && score >= goalScore ? 8 : 5))
        : 0,
      currentStreak 
    };
  } catch (error) {
    console.error('Error processing evaluation gamification:', error);
    return { success: false, error };
  }
}
